import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getGoogleFirebaseToken } from "@/lib/googleAuth";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onSessionEnded = () => setUser(null);
    window.addEventListener("auth-session-ended", onSessionEnded);
    return () => window.removeEventListener("auth-session-ended", onSessionEnded);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.verification_required) return data;
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = async () => {
    const idToken = await getGoogleFirebaseToken();
    const { data } = await api.post("/auth/google", { id_token: idToken });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const completeGoogleProfile = async (payload) => {
    const { data } = await api.put("/auth/google/profile", payload);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.verification_required) return data;
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const verifyEmail = async (email, code) => {
    const { data } = await api.post("/auth/verify-email", { email, code });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post("/auth/resend-verification", { email });
    return data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, completeGoogleProfile, register, verifyEmail, resendVerification, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
